import { FC, Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Skeleton } from '@mui/material'

import { LazyHTMLEditor } from '../../../../components/HTMLEditor/LazyHTMLEditor'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'

type AnalysisSuggestionCommentDialogProps = {
  open: boolean
  gameName?: string
  comment?: string | null
  saving?: boolean
  onClose: () => void
  onSave: (html: string) => void
}

export const AnalysisSuggestionCommentDialog: FC<AnalysisSuggestionCommentDialogProps> = ({ open, gameName, comment, saving, onClose, onSave }) => {
  const { t } = useTranslation()
  // TinyMCE normalises the stored HTML, so "unchanged" is compared with what the editor holds right after it loads.
  const [baseline, setBaseline] = useState<string>()
  const [content, setContent] = useState('')

  useEffect(() => {
    if (open) {
      setBaseline(undefined)
      setContent(comment ?? '')
    }
  }, [open, comment])

  const changed = baseline !== undefined && content !== baseline

  return (
    <InternalFormDialog
      open={open}
      title={t('internal-analysts:analysis_suggestions_comment_title', { name: gameName ?? '---' })}
      onClose={onClose}
      onSave={() => onSave(content)}
      saveDisabled={!changed}
      isSaving={saving}
      maxWidth="md"
    >
      {open && (
        <Suspense fallback={<Skeleton variant="rectangular" height={240} />}>
          <LazyHTMLEditor
            initialValue={comment ?? ''}
            onInit={(_event, editor) => {
              const loaded = editor.getContent()
              setBaseline(loaded)
              setContent(loaded)
            }}
            onEditorChange={(value) => setContent(value)}
            disabled={saving}
            init={{
              menubar: false,
              plugins: ['lists', 'autoresize'],
              toolbar: 'undo redo | blocks blockquote | bold italic underline | bullist numlist | outdent indent',
              block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4',
              min_height: 240,
              convert_urls: false,
            }}
          />
        </Suspense>
      )}
    </InternalFormDialog>
  )
}
